// Group Data Cache Service
// Implements caching for user's group list and group details to reduce API calls

export interface CachedGroupData {
  groups: any[];
  timestamp: number;
  userId: number;
}

export interface CachedGroupDetails {
  [groupSlug: string]: {
    data: any;
    timestamp: number;
  };
}

const GROUP_LIST_CACHE_KEY = 'cosavorrei_group_list_cache';
const GROUP_DETAILS_CACHE_KEY = 'cosavorrei_group_details_cache';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds
const GROUP_DETAILS_CACHE_DURATION = 15 * 60 * 1000; // 15 minutes for group details

export class GroupCacheService {
  /**
   * Cache user's group list
   */
  static cacheUserGroups(userId: number, groups: any[]): void {
    try {
      const cacheData: CachedGroupData = {
        groups,
        timestamp: Date.now(),
        userId,
      };
      
      sessionStorage.setItem(GROUP_LIST_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache user groups:', error);
    }
  }

  /**
   * Get cached user groups if valid
   */
  static getCachedUserGroups(userId: number): any[] | null {
    try {
      const cached = sessionStorage.getItem(GROUP_LIST_CACHE_KEY);
      if (!cached) return null;

      const cacheData: CachedGroupData = JSON.parse(cached);
      
      // Check if cache is for the current user
      if (cacheData.userId !== userId) {
        this.clearGroupListCache();
        return null;
      }

      // Check if cache is still valid
      const now = Date.now();
      if (now - cacheData.timestamp > CACHE_DURATION) {
        this.clearGroupListCache();
        return null;
      }

      return cacheData.groups;
    } catch (error) {
      console.warn('Failed to read cached user groups:', error);
      this.clearGroupListCache();
      return null;
    }
  }

  /**
   * Clear user groups cache
   */
  static clearGroupListCache(): void {
    try {
      sessionStorage.removeItem(GROUP_LIST_CACHE_KEY);
    } catch (error) {
      console.warn('Failed to clear group list cache:', error);
    }
  }

  /**
   * Cache group details
   */
  static cacheGroupDetails(groupSlug: string, data: any): void {
    try {
      let cachedDetails: CachedGroupDetails = {};
      
      const existing = sessionStorage.getItem(GROUP_DETAILS_CACHE_KEY);
      if (existing) {
        cachedDetails = JSON.parse(existing);
      }

      cachedDetails[groupSlug] = {
        data,
        timestamp: Date.now(),
      };

      sessionStorage.setItem(GROUP_DETAILS_CACHE_KEY, JSON.stringify(cachedDetails));
    } catch (error) {
      console.warn('Failed to cache group details:', error);
    }
  }

  /**
   * Get cached group details if valid
   */
  static getCachedGroupDetails(groupSlug: string): any | null {
    try {
      const cached = sessionStorage.getItem(GROUP_DETAILS_CACHE_KEY);
      if (!cached) return null;

      const cachedDetails: CachedGroupDetails = JSON.parse(cached);
      const groupCache = cachedDetails[groupSlug];
      
      if (!groupCache) return null;

      // Check if cache is still valid
      const now = Date.now();
      if (now - groupCache.timestamp > GROUP_DETAILS_CACHE_DURATION) {
        this.clearGroupDetailsCache(groupSlug);
        return null;
      }

      return groupCache.data;
    } catch (error) {
      console.warn('Failed to read cached group details:', error);
      this.clearGroupDetailsCache(groupSlug);
      return null;
    }
  }

  /**
   * Clear specific group details cache
   */
  static clearGroupDetailsCache(groupSlug?: string): void {
    try {
      if (!groupSlug) {
        // Clear all group details cache
        sessionStorage.removeItem(GROUP_DETAILS_CACHE_KEY);
        return;
      }

      const cached = sessionStorage.getItem(GROUP_DETAILS_CACHE_KEY);
      if (!cached) return;

      const cachedDetails: CachedGroupDetails = JSON.parse(cached);
      delete cachedDetails[groupSlug];

      if (Object.keys(cachedDetails).length === 0) {
        sessionStorage.removeItem(GROUP_DETAILS_CACHE_KEY);
      } else {
        sessionStorage.setItem(GROUP_DETAILS_CACHE_KEY, JSON.stringify(cachedDetails));
      }
    } catch (error) {
      console.warn('Failed to clear group details cache:', error);
    }
  }

  /**
   * Clear all caches (called on logout or user change)
   */
  static clearAllCaches(): void {
    this.clearGroupListCache();
    this.clearGroupDetailsCache();
  }

  /**
   * Invalidate cache when group data changes (less aggressive)
   */
  static invalidateGroupCache(groupSlug: string): void {
    // Only invalidate the specific group cache, not the entire list
    this.clearGroupDetailsCache(groupSlug);
  }

  /**
   * Invalidate cache when critical group data changes
   */
  static invalidateAllGroupCache(): void {
    this.clearGroupDetailsCache();
    this.clearGroupListCache();
  }

  /**
   * Check if group list cache exists and is valid
   */
  static hasValidGroupListCache(userId: number): boolean {
    const cached = this.getCachedUserGroups(userId);
    return cached !== null;
  }

  /**
   * Check if group details cache exists and is valid
   */
  static hasValidGroupDetailsCache(groupSlug: string): boolean {
    const cached = this.getCachedGroupDetails(groupSlug);
    return cached !== null;
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheStats(): {
    groupListCached: boolean;
    groupDetailsCacheCount: number;
    groupListAge?: number;
  } {
    try {
      const groupListCached = !!sessionStorage.getItem(GROUP_LIST_CACHE_KEY);
      
      let groupDetailsCacheCount = 0;
      let groupListAge: number | undefined;

      if (groupListCached) {
        const cached = sessionStorage.getItem(GROUP_LIST_CACHE_KEY);
        if (cached) {
          const cacheData: CachedGroupData = JSON.parse(cached);
          groupListAge = Date.now() - cacheData.timestamp;
        }
      }

      const detailsCache = sessionStorage.getItem(GROUP_DETAILS_CACHE_KEY);
      if (detailsCache) {
        const cachedDetails: CachedGroupDetails = JSON.parse(detailsCache);
        groupDetailsCacheCount = Object.keys(cachedDetails).length;
      }

      return {
        groupListCached,
        groupDetailsCacheCount,
        groupListAge,
      };
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
      return {
        groupListCached: false,
        groupDetailsCacheCount: 0,
      };
    }
  }
}